import { useChat } from '../../libs/hooks/useChat';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { IChatKeyword } from '../../libs/models/conversations/ChatKeyword';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { setSelectedPlugin } from '../../redux/features/app/pluginsSlice';
import { ChatMessageType } from '../../libs/models/conversations/ChatMessage';
import { RootState } from '../../redux/app/store';
import { IAskVariables } from '../../libs/semantic-kernel/model/Ask';
import { setSelectedModel } from '../../redux/features/app/aiModelsSlice';
import { AIModelName } from '../../libs/models/app/AIModel';
import { ChatItTrend } from './ChatItTrend';
import { ChatKeyword } from './ChatKeyword';
import { ChatVisual } from './ChatVisual';
import { IChatVisual } from '../../libs/models/users/ChatVisual';
import { useUser } from '../../libs/hooks/useUser';

// 메인 화면 노출 영역 전환. TRUE면 IT 트렌드 뉴스, 아니면 기존 추천질의.
const isItTrend = process.env.REACT_APP_IT_TREND === 'TRUE';

export interface GetResponseOptions {
  messageType: ChatMessageType;
  value: string;
  chatId: string;
  sessionId: string;
  model: string;
  plugin?: string;
  agentId?: string;
  kernelArguments?: IAskVariables[];
  processPlan?: boolean;
  webSearchOn: boolean;
}

interface ChatInsightProps {
  onSubmit: (options: GetResponseOptions) => void;
  isInsightLoading: boolean;
  setIsInsightLoading: (value: boolean) => void;
}

export const ChatInsight: FC<ChatInsightProps> = ({ onSubmit, isInsightLoading, setIsInsightLoading }) => {
  const chat = useChat();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { selectedId } = useAppSelector((state: RootState) => state.conversations_v2);

  const [keywordList, setChatKeywordList] = useState<IChatKeyword[]>();
  const [visual, setChatVisual] = useState<IChatVisual | null>(null);
  const [isDisabled, setisDisabled] = useState<boolean>(false);
  // const [isDesktop, setIsDesktop] = useState(!checkMobileKate() || window.innerWidth <= 768);

  const isDesktop = useAppSelector((state: RootState) => state.app.viewType == 'Desktop');
  useEffect(() => {
    //setIsDesktop(window.innerWidth > 768);

    setIsInsightLoading(true);
    setisDisabled(false);
    void loadInsight();
  }, []);

  useEffect(() => {
    if (isDesktop) {
      void loadChatVisual();
    }
  }, [isDesktop]);

  const loadInsight = async () => {
    await Promise.all([loadChatVisual(), loadChatKeyword()]);
  };

  const loadChatKeyword = async () => {
    // IT 트렌드 모드에서는 추천질의를 노출하지 않으므로 조회하지 않는다.
    if (isItTrend) return;

    try {
      const result = await chat.getChatKeywords();
      setChatKeywordList(result);
    } catch (error) {
      console.error('Error loading chat keywords:', error); // 에러를 콘솔에 로깅
    }
  };

  const loadChatVisual = async () => {
    try {
      const result = await user.getChatVisual();
      setChatVisual(result ?? null);
    } catch (error) {
      console.error('Error loading chat visual:', error); // 에러를 콘솔에 로깅
    } finally {
      setIsInsightLoading(false);
    }
  };

  const sendMessage = (item: IChatKeyword) => {
    if (item.pluginId) dispatch(setSelectedPlugin(item.pluginId));
    dispatch(setSelectedModel(item.modelName as string));
    setisDisabled(true);
    onSubmit({
      value: item.content,
      messageType: ChatMessageType.ChatMessage,
      chatId: selectedId,
      sessionId: selectedId,
      model: item.modelName as AIModelName,
      agentId: item.pluginId,
      plugin: item.pluginId,
      webSearchOn: item.pluginId == 'Plugin:AiSearch',
    });
  };

  return (
    <div className={classNames('chat-insight', { 'is-it-trend': isItTrend })}>
      {isInsightLoading ? (
        <>
          <a className="ds-link no-click">
            <div className="chat-visual is-skeleton">
              <div className="visual"></div>
              <div className="visual-text">
                <h2 className="title"></h2>
                <p className="desc"></p>
              </div>
            </div>
          </a>
          {isItTrend ? (
            <div className="trend-card is-skeleton">
              <div className="trend-head">
                <h2 className="trend-title"></h2>
              </div>
              <div className="trend-list"></div>
            </div>
          ) : (
            <div className="skeleton-keyword">
              <span className="item"></span>
              <span className="item"></span>
              <span className="item"></span>
              <span className="item"></span>
              <span className="item"></span>
              <span className="control"></span>
            </div>
          )}
        </>
      ) : (
        <>
          {visual && <ChatVisual data={visual} />}
          {isItTrend ? (
            <ChatItTrend isDisabled={isDisabled} sendMessage={sendMessage} />
          ) : (
            <ChatKeyword
              data={keywordList}
              isDisabled={isDisabled}
              sendMessage={sendMessage}
              loadChatKeyword={() => {
                void loadInsight();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
